import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { NEIGHBORHOODS } from '../data/neighborhoods';
import { GUIDES } from '../data/guides';
import SEOHelmet from './SEOHelmet';
import FleetGiovanniNavigation from './FleetGiovanniNavigation';
import FeaturedProperties from './FeaturedProperties';
import EligiblePropertiesGallery from './EligiblePropertiesGallery';
import ZillowReviewWidget from './ZillowReviewWidget';
import HoneypotField, { withHoneypotValue } from './HoneypotField';
import { submitChatLead } from '../services/chatLeadService';
import './GiovanniSite.css';

const words = {
  en: {
    eyebrow: 'Metro Atlanta · Real estate in your language',
    titleA: 'Your next move,', titleB: 'clearly guided.',
    intro: 'Twenty-six years of experience, multilingual guidance, and a direct path from first conversation to closing.',
    search: 'Search metro Atlanta homes', talk: 'Start a conversation',
    scroll: 'Discover HOUSEITGOING',
    experience: 'Years of experience', languages: 'Languages spoken', broker: 'Georgia broker',
    storyKicker: 'Experience with a personal point of view',
    storyTitle: 'Real estate guidance grounded in clarity, trust, and family.',
    storyBody: 'Giovanni Triana is the broker of HOUSEITGOING LLC. He helps buyers and sellers understand the decisions in front of them, compare their options, and move forward with a plan that fits their lives. Service is available in English, Spanish, and Portuguese.',
    meet: 'Meet the team',
    financingKicker: 'A no-money-down path to homeownership',
    financingTitle: 'Eligible homes with no money down.',
    financingBody: 'A select group of properties may qualify for preferred financing designed to reduce the cash needed upfront. Giovanni can help you identify eligible addresses, understand the requirements, and take the next step with confidence.',
    financingBadge: 'minimum down payment',
    financingLabel: 'Select properties only',
    financingPoints: ['Property eligibility verified by address', 'Borrower and lender approval required', 'Current program terms reviewed with you'],
    financingCta: 'Request the eligible-home list',
    financingNote: 'Financing is subject to lender approval, borrower and property eligibility, program availability, and current terms. This is not a commitment to lend.',
    cashbackKicker: 'A buyer benefit worth exploring',
    cashbackTitle: 'Buy your home. Get cash back.',
    cashbackBody: 'On qualifying purchases, HOUSEITGOING may offer part of the brokerage compensation it receives as a buyer rebate or approved closing credit. Your estimated benefit is explained before you rely on it—clearly and in writing.',
    cashbackTag: 'Qualifying home purchases',
    cashbackCta: 'See how the cash-back deal works',
    cashbackNote: 'Availability and value depend on the transaction and required approvals.',
    valueKicker: 'A clearer starting point for sellers',
    valueTitle: 'See how much your home is worth.',
    valueBody: 'Receive a personalized home-value estimate informed by the property, the neighborhood, and current metro Atlanta market activity.',
    valueCta: 'Get my free estimate',
    valueNote: 'Confidential · Local · No obligation',
    valueModalTitle: 'Request your home-value estimate',
    valueModalBody: 'Tell Giovanni where to reach you and which ZIP code the property is in.',
    valueName: 'Full name', valueEmail: 'Email address', valuePhone: 'Phone number', valueZip: 'Property ZIP code',
    valueSubmit: 'Request my estimate', valueSubmitting: 'Sending…', valueCancel: 'Cancel',
    valueSuccessTitle: 'Your request is in.',
    valueSuccessBody: 'Giovanni will personally follow up about your free home-value estimate.',
    valueClose: 'Close',
    valueError: 'We could not send your request. Please try again.',
    areasKicker: 'Across metro Atlanta', areasTitle: 'Find the community that fits your next chapter.',
    areasBody: 'Explore local context, housing choices, and practical buyer guidance for the communities Giovanni serves.',
    guidesKicker: 'Knowledge before pressure', guidesTitle: 'Answers for the questions that shape a move.',
    cta: 'Ready to talk about your next move?', ctaBody: 'Choose the language you prefer. Giovanni will help you build the next step from there.'
  },
  es: {
    eyebrow: 'Área de Atlanta · Bienes raíces en su idioma',
    titleA: 'Su próximo paso,', titleB: 'con orientación clara.',
    intro: 'Veintiséis años de experiencia, atención multilingüe y un camino directo desde la primera conversación hasta el cierre.',
    search: 'Buscar casas en Atlanta', talk: 'Iniciar una conversación',
    scroll: 'Conozca HOUSEITGOING',
    experience: 'Años de experiencia', languages: 'Idiomas', broker: 'Corredor en Georgia',
    storyKicker: 'Experiencia con un enfoque personal',
    storyTitle: 'Orientación inmobiliaria basada en claridad, confianza y familia.',
    storyBody: 'Giovanni Triana es el corredor de HOUSEITGOING LLC. Ayuda a compradores y vendedores a comprender sus decisiones, comparar opciones y avanzar con un plan que se adapte a sus vidas. La atención está disponible en inglés, español y portugués.',
    meet: 'Conozca al equipo',
    financingKicker: 'Una ruta sin pago inicial hacia su hogar',
    financingTitle: 'Viviendas elegibles sin pago inicial.',
    financingBody: 'Un grupo selecto de propiedades puede calificar para financiamiento preferencial diseñado para reducir el dinero necesario al inicio. Giovanni puede ayudarle a identificar direcciones elegibles, entender los requisitos y avanzar con confianza.',
    financingBadge: 'pago inicial mínimo',
    financingLabel: 'Solo propiedades seleccionadas',
    financingPoints: ['Elegibilidad verificada por dirección', 'Se requiere aprobación del prestatario y prestamista', 'Revisión de las condiciones vigentes del programa'],
    financingCta: 'Solicitar la lista de viviendas elegibles',
    financingNote: 'El financiamiento está sujeto a aprobación del prestamista, elegibilidad del prestatario y de la propiedad, disponibilidad y condiciones vigentes. Esto no constituye un compromiso de préstamo.',
    cashbackKicker: 'Un beneficio para compradores que vale la pena explorar',
    cashbackTitle: 'Compre su hogar. Reciba dinero de vuelta.',
    cashbackBody: 'En compras que califican, HOUSEITGOING puede ofrecer parte de la compensación que recibe la correduría como reembolso para el comprador o crédito aprobado para el cierre. Su beneficio estimado se explica claramente y por escrito antes de que dependa de él.',
    cashbackTag: 'Compras de vivienda que califican',
    cashbackCta: 'Conozca cómo funciona la oferta',
    cashbackNote: 'La disponibilidad y el valor dependen de la transacción y de las aprobaciones requeridas.',
    valueKicker: 'Un punto de partida más claro para vendedores',
    valueTitle: 'Descubra cuánto vale su casa.',
    valueBody: 'Reciba una estimación personalizada basada en la propiedad, el vecindario y la actividad actual del mercado de Atlanta.',
    valueCta: 'Obtener mi estimación gratis',
    valueNote: 'Confidencial · Local · Sin compromiso',
    valueModalTitle: 'Solicite la estimación de su casa',
    valueModalBody: 'Indique cómo comunicarnos con usted y el código postal de la propiedad.',
    valueName: 'Nombre completo', valueEmail: 'Correo electrónico', valuePhone: 'Número de teléfono', valueZip: 'Código postal de la propiedad',
    valueSubmit: 'Solicitar mi estimación', valueSubmitting: 'Enviando…', valueCancel: 'Cancelar',
    valueSuccessTitle: 'Recibimos su solicitud.',
    valueSuccessBody: 'Giovanni se comunicará personalmente con usted acerca de su estimación gratuita.',
    valueClose: 'Cerrar',
    valueError: 'No pudimos enviar su solicitud. Inténtelo de nuevo.',
    areasKicker: 'En toda el área de Atlanta', areasTitle: 'Encuentre la comunidad ideal para su próximo capítulo.',
    areasBody: 'Explore el contexto local, las opciones de vivienda y orientación práctica para las comunidades que Giovanni atiende.',
    guidesKicker: 'Conocimiento antes que presión', guidesTitle: 'Respuestas para las preguntas que definen una mudanza.',
    cta: '¿Listo para hablar sobre su próximo paso?', ctaBody: 'Elija el idioma que prefiera. Giovanni le ayudará a definir el siguiente paso.'
  }
};

const financingImages = [
  '/areas/atlanta-luxury-interior.jpg',
  '/areas/norcross-luxury-interior.jpg',
  '/areas/peachtree-corners-luxury-interior.jpg',
  '/areas/lawrenceville-luxury-interior.jpg',
  '/areas/suwanee-luxury-interior.jpg',
  '/areas/alpharetta-luxury-interior.jpg'
];

const ORIGIN = `https://${SITE_CONFIG.BASE_URL.replace(/^https?:\/\//, '').replace(/^www\./, '')}`;

export default function GiovanniHome() {
  const { language, t } = useLanguage();
  const c = words[language];
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);
  const [showHomeValueForm, setShowHomeValueForm] = useState(false);
  const [homeValueStatus, setHomeValueStatus] = useState('idle');
  const [homeValueForm, setHomeValueForm] = useState({ name: '', email: '', phoneNumber: '', postalCode: '' });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.load();
    video.play().catch(() => {});
  }, []);

  useEffect(() => {
    if (!showHomeValueForm) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && homeValueStatus !== 'submitting') setShowHomeValueForm(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showHomeValueForm, homeValueStatus]);

  const openHomeValueForm = () => {
    setHomeValueStatus('idle');
    setShowHomeValueForm(true);
  };

  const updateHomeValueField = (event) => {
    const { name, value } = event.target;
    setHomeValueForm((current) => ({ ...current, [name]: value }));
  };

  const handleHomeValueSubmit = async (event) => {
    event.preventDefault();
    setHomeValueStatus('submitting');

    try {
      const response = await submitChatLead(withHoneypotValue({
        ...homeValueForm,
        serviceReq: 'estimate',
        siteId: SITE_CONFIG.SITE_ID
      }, event.currentTarget));

      if (!response.ok) throw new Error(`Home-value lead failed with status ${response.status}`);
      setHomeValueForm({ name: '', email: '', phoneNumber: '', postalCode: '' });
      setHomeValueStatus('success');
    } catch (error) {
      console.error('Home-value lead submission failed:', error);
      setHomeValueStatus('error');
    }
  };

  return (
    <div className="gt-site landing-page">
      <SEOHelmet
        title="Giovanni Triana - Metro Atlanta Real Estate Broker | HOUSEITGOING LLC"
        description="Search metro Atlanta homes with Giovanni Triana, bilingual broker of HOUSEITGOING LLC. English, Spanish, and Portuguese real estate guidance."
        path="/"
        jsonLd={{ '@context': 'https://schema.org', '@type': 'WebSite', name: SITE_CONFIG.AGENT.BROKERAGE, url: ORIGIN, inLanguage: ['en', 'es'] }}
      />
      <section className="gt-hero">
        <video
          ref={videoRef}
          className={`gt-hero-video${videoReady ? ' is-ready' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onCanPlay={() => setVideoReady(true)}
        >
          <source src={SITE_CONFIG.LANDING_PAGE.VIDEOS.HERO} type="video/mp4" />
        </video>
        <div className="gt-hero-shade" />
        <FleetGiovanniNavigation />
        <div className="gt-hero-copy">
          <span className="gt-kicker">{c.eyebrow}</span>
          <h1>{c.titleA}<br /><em>{c.titleB}</em></h1>
          <p>{c.intro}</p>
          <div className="gt-actions">
            <Link className="gt-button gt-button--gold" to="/search">{c.search} <span>→</span></Link>
            <Link className="gt-button gt-button--glass" to="/chat">{c.talk}</Link>
          </div>
        </div>
        <a className="gt-scroll" href="#story"><span />{c.scroll}</a>
      </section>

      <section className="gt-proof" aria-label="Professional credentials">
        <div><strong>26</strong><span>{c.experience}</span></div>
        <div><strong>3</strong><span>{c.languages}</span></div>
        <div><strong>#221561</strong><span>{c.broker}</span></div>
      </section>

      <section className="gt-story gt-section" id="story">
        <div className="gt-story-art">
          <span className="gt-photo-frame"><img src="/giovanni-family.png" alt="Giovanni and Monica Triana" /></span>
          <span className="gt-story-number">26<small>{language === 'es' ? 'años' : 'years'}</small></span>
        </div>
        <div className="gt-story-copy">
          <span className="gt-kicker gt-kicker--dark">{c.storyKicker}</span>
          <h2>{c.storyTitle}</h2>
          <p>{c.storyBody}</p>
          <Link className="gt-text-link" to="/about-us">{c.meet} <span>→</span></Link>
        </div>
      </section>

      <FeaturedProperties />

      <section className="gt-financing gt-section" aria-labelledby="gt-financing-title">
        <div className="gt-financing-copy">
          <span className="gt-kicker gt-kicker--dark">{c.financingKicker}</span>
          <div className="gt-financing-rate"><strong>$0</strong><span>{c.financingBadge}</span></div>
          <h2 id="gt-financing-title">{c.financingTitle}</h2>
          <p>{c.financingBody}</p>
          <ul>
            {c.financingPoints.map((point) => <li key={point}><span>✓</span>{point}</li>)}
          </ul>
          <Link className="gt-button gt-button--navy" to="/contact-us">{c.financingCta} <span>→</span></Link>
          <small>{c.financingNote}</small>
        </div>
        <EligiblePropertiesGallery />
      </section>

      <section className="gt-cashback-teaser" aria-labelledby="gt-cashback-title">
        <div className="gt-cashback-teaser-copy">
          <span className="gt-kicker">{c.cashbackKicker}</span>
          <h2 id="gt-cashback-title">{c.cashbackTitle}</h2>
          <p>{c.cashbackBody}</p>
          <Link className="gt-button gt-button--gold" to="/cash-back-deal">{c.cashbackCta} <span>→</span></Link>
          <small>{c.cashbackNote}</small>
        </div>
        <div className="gt-cashback-mark" aria-hidden="true">
          <span>{language === 'es' ? 'Dinero' : 'Cash'}</span>
          <strong>{language === 'es' ? 'de vuelta' : 'Back'}</strong>
          <em>{c.cashbackTag}</em>
        </div>
      </section>

      <section className="gt-home-value" aria-labelledby="gt-home-value-title">
        <div className="gt-home-value-image" aria-hidden="true">
          <img src="/areas/atlanta-luxury-interior.jpg" alt="" loading="lazy" decoding="async" />
        </div>
        <div className="gt-home-value-copy">
          <span className="gt-kicker gt-kicker--dark">{c.valueKicker}</span>
          <h2 id="gt-home-value-title">{c.valueTitle}</h2>
          <p>{c.valueBody}</p>
          <button className="gt-button gt-button--navy" type="button" onClick={openHomeValueForm}>
            {c.valueCta} <span>→</span>
          </button>
          <small>{c.valueNote}</small>
        </div>
      </section>

      <section className="gt-areas gt-section">
        <header className="gt-section-head">
          <span className="gt-kicker gt-kicker--dark">{c.areasKicker}</span>
          <h2>{c.areasTitle}</h2>
          <p>{c.areasBody}</p>
        </header>
        <div className="gt-area-grid">
          {NEIGHBORHOODS.slice(0, 6).map((area, index) => (
            <Link className="gt-area-card gt-area-card--photo" to={`/area/${area.slug}`} key={area.slug}>
              <img
                className="gt-area-card-image"
                src={financingImages[index]}
                alt={language === 'es' ? `Interior de una casa de lujo en ${area.name}` : `${area.name} luxury home interior`}
                loading="lazy"
                decoding="async"
              />
              <span className="gt-area-card-shade" aria-hidden="true" />
              <span className="gt-area-index">0{index + 1}</span>
              <h3>{area.name}</h3>
              <p>{area.tagline[language]}</p>
              <span className="gt-card-arrow">↗</span>
            </Link>
          ))}
        </div>
        <Link className="gt-text-link" to="/areas">{t('explore')} {t('areas')} <span>→</span></Link>
      </section>

      <section className="gt-guides gt-section">
        <header className="gt-section-head gt-section-head--light">
          <span className="gt-kicker">{c.guidesKicker}</span>
          <h2>{c.guidesTitle}</h2>
        </header>
        <div className="gt-guide-row">
          {GUIDES.slice(0, 4).map((item, index) => (
            <Link to={`/guide/${item.slug}`} key={item.slug}>
              <span>0{index + 1}</span><h3>{item.title[language]}</h3><em>→</em>
            </Link>
          ))}
        </div>
        <Link className="gt-text-link gt-text-link--light" to="/guides">{t('explore')} {t('guides')} <span>→</span></Link>
      </section>

      <ZillowReviewWidget />

      <section className="gt-final-cta">
        <img src="/giovanni-family.png" alt={language === 'es' ? 'Giovanni y Monica Triana' : 'Giovanni and Monica Triana'} />
        <div><span className="gt-kicker">HOUSEITGOING LLC</span><h2>{c.cta}</h2><p>{c.ctaBody}</p><Link className="gt-button gt-button--gold" to="/contact-us">{t('workTogether')} <span>→</span></Link></div>
      </section>

      {showHomeValueForm && (
        <div
          className="gt-value-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && homeValueStatus !== 'submitting') setShowHomeValueForm(false);
          }}
        >
          <div className="gt-value-modal" role="dialog" aria-modal="true" aria-labelledby="gt-value-modal-title">
            <button
              className="gt-value-modal-close"
              type="button"
              aria-label={c.valueClose}
              onClick={() => setShowHomeValueForm(false)}
              disabled={homeValueStatus === 'submitting'}
            >
              ×
            </button>
            {homeValueStatus === 'success' ? (
              <div className="gt-value-success" role="status">
                <span aria-hidden="true">✓</span>
                <h2 id="gt-value-modal-title">{c.valueSuccessTitle}</h2>
                <p>{c.valueSuccessBody}</p>
                <button className="gt-button gt-button--navy" type="button" onClick={() => setShowHomeValueForm(false)}>{c.valueClose}</button>
              </div>
            ) : (
              <>
                <span className="gt-kicker gt-kicker--dark">HOUSEITGOING LLC</span>
                <h2 id="gt-value-modal-title">{c.valueModalTitle}</h2>
                <p>{c.valueModalBody}</p>
                <form onSubmit={handleHomeValueSubmit}>
                  <HoneypotField />
                  <label>{c.valueName}<input name="name" type="text" autoComplete="name" value={homeValueForm.name} onChange={updateHomeValueField} required /></label>
                  <label>{c.valueEmail}<input name="email" type="email" autoComplete="email" value={homeValueForm.email} onChange={updateHomeValueField} required /></label>
                  <label>{c.valuePhone}<input name="phoneNumber" type="tel" autoComplete="tel" value={homeValueForm.phoneNumber} onChange={updateHomeValueField} required /></label>
                  <label>{c.valueZip}<input name="postalCode" type="text" inputMode="numeric" autoComplete="postal-code" value={homeValueForm.postalCode} onChange={updateHomeValueField} required /></label>
                  {homeValueStatus === 'error' && <p className="gt-value-form-error" role="alert">{c.valueError}</p>}
                  <div className="gt-value-form-actions">
                    <button className="gt-button gt-button--navy" type="submit" disabled={homeValueStatus === 'submitting'}>
                      {homeValueStatus === 'submitting' ? c.valueSubmitting : c.valueSubmit}
                    </button>
                    <button className="gt-value-cancel" type="button" onClick={() => setShowHomeValueForm(false)} disabled={homeValueStatus === 'submitting'}>{c.valueCancel}</button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
